import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MapPin, X } from 'lucide-react';
import api from '../../api';

// Tidy a GNAF address for display/storage: "UNIT 1/32 PORTER ROAD, HEIDELBERG
// HEIGHTS VIC 3081" -> "1/32 Porter Road, Heidelberg Heights VIC 3081". Drops the
// leading "UNIT " (the CRM stores the sub-dwelling as "1/32", not "UNIT 1/32")
// and title-cases everything except the state and postcode.
const STATE_CODES = new Set(['VIC', 'NSW', 'QLD', 'SA', 'WA', 'TAS', 'ACT', 'NT']);
export const prettyGnafAddress = (raw) => {
  let s = String(raw || '').trim().replace(/^UNIT\s+/i, '');
  return s.replace(/[A-Za-z]+/g, (w) => {
    const up = w.toUpperCase();
    if (STATE_CODES.has(up)) return up;
    return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
  });
};

// Address suggestions come from our own GNAF endpoint first, then Mapbox (the app already
// carries a Mapbox token that works for the map views). The old Addressr (RapidAPI)
// fallback is gone: its key had to ship in the browser bundle, where anyone could lift it.
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';

// Pull suburb / state / postcode out of a Mapbox geocoding feature so the parent form can
// auto-fill those fields when a suggestion is picked. In Australia the suburb is the
// `locality` (falling back to `place`); the state comes from the region's short_code
// (e.g. "AU-VIC" -> "VIC"); the postcode is the `postcode` context entry.
const parseMapboxFeature = (feature) => {
  const context = Array.isArray(feature?.context) ? feature.context : [];
  const byPrefix = (prefix) => context.find((c) => typeof c?.id === 'string' && c.id.startsWith(prefix));

  const locality = byPrefix('locality');
  const place = byPrefix('place');
  const region = byPrefix('region');
  const postcodeCtx = byPrefix('postcode');

  let state = '';
  const shortCode = region?.short_code || '';
  if (shortCode.includes('-')) {
    state = shortCode.split('-')[1].toUpperCase(); // "AU-VIC" -> "VIC"
  } else if (region?.text) {
    state = region.text;
  }

  return {
    suburb: locality?.text || place?.text || '',
    state,
    postcode: postcodeCtx?.text || '',
  };
};

// Primary source: the GNAF-backed address autocomplete already used by the
// Buyer Portal's Property Research page (suburb_intelligence). Unlike Mapbox,
// GNAF carries sub-dwelling/unit numbers, so "1/32 ..." and every unit in a
// block actually appear in the dropdown (CRM-2026-00038). Returns
// {results:[{address, property_id}]}; fails open to [] so Mapbox can take over.
const searchWithGnaf = async (query) => {
  try {
    const res = await api.get('/api/suburb-intelligence/address-autocomplete/', { params: { q: query } });
    const results = Array.isArray(res?.data?.results) ? res.data.results : [];
    return results
      .filter((r) => r && r.address)
      .map((r, index) => ({
        description: prettyGnafAddress(r.address),
        place_id: r.property_id || `gnaf-${index}`,
        // `gnaf: true` tells the parent this description is the full,
        // authoritative address (unit included), so it should be used as-is
        // rather than falling back to the typed-unit reconstruction.
        raw: { gnaf: true, property_id: r.property_id, gnaf_address: r.address },
      }));
  } catch (err) {
    return [];
  }
};

// Fallback source: Mapbox forward geocoding (autocomplete), restricted to Australia.
const searchWithMapbox = async (query) => {
  const url =
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json` +
    `?access_token=${MAPBOX_TOKEN}&country=au&autocomplete=true&limit=8&types=address`;
  const response = await fetch(url);
  if (!response.ok) return [];
  const data = await response.json();
  const features = Array.isArray(data?.features) ? data.features : [];
  return features.map((feature, index) => {
    const parsed = parseMapboxFeature(feature);
    // Strip the trailing ", Australia" for a cleaner display string.
    const description = String(feature?.place_name || '').replace(/,\s*Australia$/i, '');
    return {
      description,
      place_id: feature?.id || `mb-${index}`,
      // Normalise onto the same shape the parent's onSelect already reads from Addressr,
      // so picking a suggestion auto-fills suburb / state / postcode without any change there.
      raw: {
        ...feature,
        suburb: parsed.suburb,
        state: parsed.state,
        postcode: parsed.postcode,
      },
    };
  });
};

// GNAF first (the only source with unit-level addresses), Mapbox only if GNAF
// returned nothing. Shared with fields that merge in their own suggestions,
// such as the CMA target address.
export const suggestAddresses = async (query) => {
  const mapped = await searchWithGnaf(query);
  if (mapped.length === 0 && MAPBOX_TOKEN) return searchWithMapbox(query);
  return mapped;
};

const AddressAutocomplete = ({ value, onChange, onSelect, placeholder = "Start typing address...", className = "" }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceRef = useRef(null);

  const searchAddresses = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setSearching(true);
    try {
      const mapped = await suggestAddresses(query);
      setSuggestions(mapped);
      setShowSuggestions(mapped.length > 0);
    } catch (err) {
      console.error('Address search error:', err);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    onChange(e);

    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce the search
    debounceRef.current = setTimeout(() => {
      searchAddresses(inputValue);
    }, 300);
  };

  const handleSuggestionClick = (suggestion) => {
    if (onSelect) {
      onSelect(suggestion);
    }
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleClear = () => {
    onChange({ target: { name: 'address', value: '' } });
    setSuggestions([]);
    setShowSuggestions(false);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          name="address"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
          placeholder={placeholder}
          className={`w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] ${className}`}
          autoComplete="off"
        />
        {value && !searching && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        {searching && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-[#55C5D0] border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-2 bg-white rounded-[12px] border border-gray-200 shadow-xl max-h-[280px] overflow-y-auto"
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.place_id || index}
              type="button"
              onClick={() => handleSuggestionClick(suggestion)}
              className={`w-full text-left px-4 py-3 hover:bg-[#F0FDFE] transition-colors border-b border-gray-100 last:border-b-0 flex items-center gap-3 group ${index === selectedIndex ? 'bg-[#F0FDFE]' : ''
                }`}
            >
              <div className="p-1.5 bg-gray-100 rounded-lg group-hover:bg-[#2DC8DB]/10 transition-colors">
                <MapPin className="w-4 h-4 text-[#6B7280] group-hover:text-[#2DC8DB] flex-shrink-0" />
              </div>
              <span className="text-[13px] font-medium text-[#1a1a1a] group-hover:text-[#2DC8DB] transition-colors truncate">
                {suggestion.description}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;




